/**
 * studio-mix.js — client-side mixdown for Rammy Studio.
 *
 * The server stitches narration into a single MP3. Everything layered *under*
 * that narration is mixed here, in the browser, because the browser is where
 * the exported audio and the exported video both get assembled:
 *
 *   narration (full)  +  ambience (looped, quiet)  +  music (looped, ducked)
 *
 * Ducking is the interesting part. Music must sit under the voice and swell in
 * the pauses between paragraphs. We know exactly where speech is — the render
 * response carries every paragraph's stem plus the real gap after it — so the
 * envelope is computed, smoothed into ramps, and applied with
 * `setValueCurveAtTime`. Same envelope for playback and for export, so what
 * you hear is what you download.
 *
 * Output is WAV: no dependency-free MP3 encoder ships here, and WAV plays
 * everywhere.
 */

const FADE_SECONDS = 0.6;
const ENV_RATE = 100;          // envelope samples per second
const RAMP_SECONDS = 0.45;     // how long music takes to duck / swell

/** Decode a base64 audio payload into an AudioBuffer. Returns null on
 *  failure so callers can fall back to narration-only. */
async function decodeB64(ctx, b64) {
  if (!b64) return null;
  try {
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return await ctx.decodeAudioData(bytes.buffer.slice(0));
  } catch (e) {
    console.warn("studio-mix decode failed", e);
    return null;
  }
}

async function decodeArrayBuffer(ctx, arrayBuffer) {
  if (!arrayBuffer) return null;
  try {
    return await ctx.decodeAudioData(arrayBuffer.slice(0));
  } catch (e) {
    console.warn("studio-mix decode (buffer) failed", e);
    return null;
  }
}

/**
 * Where the speaking actually happens, derived from per-paragraph stems and
 * the real silence the server inserted after each one.
 * @returns {Promise<Array<{start:number,end:number}>>}
 */
export async function speechSegments(ctx, stems = [], gaps = []) {
  const segments = [];
  let t = 0;
  for (let i = 0; i < stems.length; i++) {
    const buf = await decodeB64(ctx, stems[i]?.audio_b64);
    const dur = buf ? buf.duration : 0;
    if (dur > 0) segments.push({ start: t, end: t + dur });
    t += dur + (gaps[i] ?? 0.4);
  }
  return segments;
}

/**
 * Build a smoothed gain envelope: `full` in the gaps, `full * (1 - duck)`
 * under speech, with ramps either side.
 * @returns {Float32Array}
 */
export function buildDuckEnvelope({ total, segments = [], full = 0.22, duck = 0.7 }) {
  const n = Math.max(2, Math.ceil(total * ENV_RATE));
  const low = full * (1 - Math.max(0, Math.min(1, duck)));
  const env = new Float32Array(n).fill(full);

  for (const seg of segments) {
    const a = Math.max(0, Math.floor(seg.start * ENV_RATE));
    const b = Math.min(n, Math.ceil(seg.end * ENV_RATE));
    for (let i = a; i < b; i++) env[i] = low;
  }

  // Box-smooth twice — turns the steps into ramps without any extra maths.
  const win = Math.max(1, Math.round(RAMP_SECONDS * ENV_RATE));
  let cur = env;
  for (let pass = 0; pass < 2; pass++) {
    const out = new Float32Array(n);
    let acc = 0;
    for (let i = 0; i < n + win; i++) {
      if (i < n) acc += cur[i];
      if (i >= win) acc -= cur[i - win];
      const idx = i - Math.floor(win / 2);
      if (idx >= 0 && idx < n) out[idx] = acc / Math.min(win, i + 1);
    }
    cur = out;
  }

  // Fade in / out at the edges so the bed enters and leaves gracefully.
  const fade = Math.min(Math.round(FADE_SECONDS * 2 * ENV_RATE), Math.floor(n / 3));
  for (let i = 0; i < fade; i++) {
    const k = i / fade;
    cur[i] *= k;
    cur[n - 1 - i] *= k;
  }
  return cur;
}

/** Loop a buffer across `total` seconds by rescheduling it — the `loop` flag
 *  is unreliable inside OfflineAudioContext in some engines. */
function scheduleLoop(ctx, buffer, target, total) {
  let cursor = 0;
  const dur = buffer.duration;
  if (dur <= 0.05) return;
  while (cursor < total) {
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(target);
    src.start(cursor);
    cursor += dur;
  }
}

/**
 * Mix narration + ambience + a ducked music bed into one WAV Blob.
 *
 * @param {object} o
 * @param {string} o.narrationB64
 * @param {string} [o.ambienceB64]
 * @param {number} [o.ambienceVolume]
 * @param {ArrayBuffer} [o.musicBuffer]  — raw bytes of the music track
 * @param {number} [o.musicVolume]
 * @param {number} [o.musicDuck]
 * @param {Array}  [o.stems]  — for the ducking envelope
 * @param {Array}  [o.gaps]
 * @returns {Promise<Blob|null>} audio/wav
 */
export async function mixFullTrack({
  narrationB64, ambienceB64 = null, ambienceVolume = 0.18,
  musicBuffer = null, musicVolume = 0.22, musicDuck = 0.7,
  stems = [], gaps = [],
}) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const decodeCtx = new AC();

  const narrationAB = await decodeB64(decodeCtx, narrationB64);
  const ambienceAB = ambienceB64 ? await decodeB64(decodeCtx, ambienceB64) : null;
  const musicAB = musicBuffer ? await decodeArrayBuffer(decodeCtx, musicBuffer) : null;
  const segments = musicAB ? await speechSegments(decodeCtx, stems, gaps) : [];
  await decodeCtx.close().catch(() => {});

  if (!narrationAB) return null;

  const rate = 44100;
  const total = narrationAB.duration + 0.25;
  const offline = new OfflineAudioContext(1, Math.ceil(total * rate), rate);

  const narSrc = offline.createBufferSource();
  narSrc.buffer = narrationAB;
  narSrc.connect(offline.destination);
  narSrc.start(0);

  if (ambienceAB && ambienceVolume > 0) {
    const ambGain = offline.createGain();
    ambGain.gain.value = ambienceVolume;
    ambGain.connect(offline.destination);
    scheduleLoop(offline, ambienceAB, ambGain, narrationAB.duration);
    const inEnd = Math.min(FADE_SECONDS, narrationAB.duration * 0.1);
    const outStart = Math.max(narrationAB.duration - FADE_SECONDS, inEnd);
    ambGain.gain.setValueAtTime(0, 0);
    ambGain.gain.linearRampToValueAtTime(ambienceVolume, inEnd);
    ambGain.gain.setValueAtTime(ambienceVolume, outStart);
    ambGain.gain.linearRampToValueAtTime(0, narrationAB.duration);
  }

  if (musicAB && musicVolume > 0) {
    const musGain = offline.createGain();
    musGain.gain.value = 0;
    musGain.connect(offline.destination);
    scheduleLoop(offline, musicAB, musGain, narrationAB.duration);
    const env = buildDuckEnvelope({
      total: narrationAB.duration,
      segments,
      full: musicVolume,
      duck: musicDuck,
    });
    musGain.gain.setValueCurveAtTime(env, 0, narrationAB.duration);
  }

  const rendered = await offline.startRendering();
  return audioBufferToWavBlob(rendered);
}

/** Back-compat wrapper — narration + ambience only. */
export async function mixNarrationWithAmbience({ narrationB64, ambienceB64, ambienceVolume = 0.18 }) {
  return mixFullTrack({ narrationB64, ambienceB64, ambienceVolume });
}

/**
 * Encode an AudioBuffer to a mono 16-bit PCM WAV Blob.
 */
function audioBufferToWavBlob(buffer) {
  const numChannels = 1;
  const sampleRate = buffer.sampleRate;
  const inCh = buffer.numberOfChannels;
  const numSamples = buffer.length;
  const bytesPerSample = 2;
  const dataSize = numSamples * numChannels * bytesPerSample;
  const bufSize = 44 + dataSize;
  const ab = new ArrayBuffer(bufSize);
  const view = new DataView(ab);
  const writeStr = (offset, s) => { for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i)); };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  const chanData = [];
  for (let ch = 0; ch < inCh; ch++) chanData.push(buffer.getChannelData(ch));
  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    let s = 0;
    for (let ch = 0; ch < inCh; ch++) s += chanData[ch][i];
    s = s / inCh;
    s = Math.max(-1, Math.min(1, s));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }
  return new Blob([ab], { type: "audio/wav" });
}
